import {Component, inject, Input, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import {
  NbButtonModule,
  NbCardModule,
  NbCheckboxModule,
  NbDialogRef,
  NbDialogService,
  NbIconModule,
  NbInputModule,
  NbOptionModule,
  NbRadioModule,
  NbSelectModule
} from '@nebular/theme';
import {TranslateModule} from '@ngx-translate/core';
import {CommonModule} from '@angular/common';
import {combineLatest, filter} from 'rxjs';
import {ErrorService, I18nService, QuotePartService, ScopeService} from '../../../../@core/utils';
import {KPI} from '../../../../@core/api';
import {idMapToArray} from '../../../../@core/utils/scope.service';
import {QuotePartItem} from "../../../../@core/api/models";

@Component({
  selector: 'app-investment-quote-part-dialog',
  standalone: true,
  imports: [
    TranslateModule,
    NbCardModule,
    FormsModule,
    ReactiveFormsModule,
    NbOptionModule,
    NbRadioModule,
    NbCheckboxModule,
    NbSelectModule,
    NbInputModule,
    NbButtonModule,
    CommonModule,
    NbIconModule
  ],
  templateUrl: './investment-quote-part-dialog.component.html',
  styleUrl: './investment-quote-part.component.scss',
})
export class InvestmentQuotePartDialogComponent implements OnInit {

  private i18nService = inject(I18nService);

  repartitionForm: FormGroup = this.fb.group({
    name: ['', [Validators.required]],
    distribution: this.fb.array([]),
    refacturable: '',
  });
  public investmentSponsors: KPI.Sponsor[];

  @Input() investment: KPI.MappedInvestment & {budget: number};
  @Input() siteId: number;
  @Input() readOnly: boolean = false;
  @ViewChild('alertDialog') dialogTemplate: TemplateRef<any>;

  constructor(
    private fb: FormBuilder,
    private ref: NbDialogRef<InvestmentQuotePartDialogComponent>,
    private errorService: ErrorService,
    private quotePartService: QuotePartService,
    private scopeService: ScopeService,
    private nbDialogService: NbDialogService
  ) {}

  public config$ = this.scopeService.config$;

  ngOnInit(): void {
    this.loadQuotePartData();
  }

  private loadQuotePartData() {
    combineLatest([
      this.scopeService.investmentSponsors$.pipe(idMapToArray()),
      this.quotePartService.getQuotePartDetail(this.investment.quote_part?.id)
    ]).pipe(
      filter(([sponsors, _]) => !!sponsors )
    ).subscribe(([investmentSponsors, quotePart]) => {
      this.investmentSponsors = investmentSponsors;
      this.repartitionForm.setControl('distribution',
        this.fb.array(
          quotePart.sponsors.map(
            dist => this.createDistributionRow(dist.id, dist.sponsor_id, dist.percentage, dist.property_code, dist.property_code_label)
          )
        )
      );
      this.repartitionForm.patchValue({
        name: quotePart.name,
        refacturable: quotePart.refacturable == true ? 'true' : 'false',
      });
      if (this.readOnly) {
        this.repartitionForm.disable();
      }
    });
  }

  get distributionControls() {
    return this.repartitionForm.get('distribution') as FormArray;
  }

  createDistributionRow(id?: number, sponsor_id?: number, percentage?: number | string, property_code?: string, property_code_label?: string) {
    return this.fb.group({
      id: [id],
      sponsor_id: [sponsor_id],
      percentage: [percentage],
      property_code: [{value: property_code, disabled: true}],
      property_code_label: [{value: property_code_label, disabled: true}]
    });
  }

  getPrice(item: AbstractControl<QuotePartItem>) {
    if (!item.value) return
    const price = this.investment.budget * item.get('percentage').value / 100;
    return this.i18nService.formatPriceWithThousands(price)
  }

  getSponsorName(item: AbstractControl<QuotePartItem>) {
    if (!item.value) return
    const sponsor = this.investmentSponsors.find(sponsor => sponsor.id === item.get('sponsor_id').value);
    return sponsor ? sponsor.name : '';
  }

  confirmOK() {
    if (this.readOnly) {
      this.ref.close();
      return;
    }

    const {distribution} = this.repartitionForm.getRawValue();
    const total = this.getTotalPercentage();
    if (total !== 100) {
      //show error message
      this.nbDialogService.open(this.dialogTemplate, {
        context: {
          message: 'quote_part.percentage_total_error'
        }
      })
      return;
    }
    const payload = {
      current_quote_part: this.investment.quote_part?.id,
      sponsors: distribution,
    };

    this.quotePartService.updateOrCreateQuotePartTypeOther(this.investment.id, payload as any).subscribe(() => {
      this.ref.close({
        reload: true
      });
    }, (err) => this.errorService.handleError(err, true));

  }

  get isUpdateBtnEnabled() {
    return this.repartitionForm.dirty;
  }

  getTotalPercentage(): number | string {
    const total = this.distributionControls.value.reduce((acc, dist) => {
      return acc + parseFloat(dist?.percentage)
    }, 0);
    return isNaN(total) ? '-' : total;
  }

  close() {
    this.ref.close()
  }
}
